import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import { PaneTitle } from './PaneTitle';
import ModalContext from './ModalContext';
import { DropdownParentContextProvider, mainTheme } from '@plecto/ui';

const StyledModalContent = styled.div`
  position: relative;
  background-color: ${mainTheme.surface[2]};
  color: ${mainTheme.text.primary};
  border: 1px solid ${mainTheme.border.decorative};
  border-radius: 2px;
  box-shadow: 0 0 30px 2px rgba(0, 0, 0, 0.50);
  background-clip: padding-box;
  outline: 0;
  padding: 0;
  font-size: 14px;
`;

type Props = {
    className?: string,
    badge?: string,
    size?: string,
    onClose: () => void,
    title?: JSX.Element | string,
    hidden?: boolean,
    blankMode?: boolean,
    forceTop?: boolean,
    closeOnBackdropClick?: boolean,
};

export function PaneModal({
	className,
	badge,
	size,
	onClose,
	title,
	hidden: initHidden = false,
	blankMode,
	forceTop,
	children,
	closeOnBackdropClick,
}: PropsWithChildren<Props>) {

	const [hidden, setHidden] = useState(initHidden);
	// Dropdown menus portal to body by default, which sits under the modal. Open them in here instead.
	const [menuParent, setMenuParent] = useState<HTMLDivElement | null>(null);

	const onHide = useCallback(() => {
		setHidden(true);
	}, []);

	const onShow = useCallback(() => {
		setHidden(false);
	}, []);

	const context = useMemo(() => ({
		hide: onHide,
		show: onShow
	}), [onHide, onShow]);

	return <ModalContext.Provider value={context}>
		<Modal
			className={className}
			size={size}
			hidden={hidden}
			onClose={onClose}
			closeOnBackdropClick={closeOnBackdropClick}
			forceTop={forceTop}
		>
			<StyledModalContent className="modal-content" ref={setMenuParent}>
				<PaneTitle
					blankMode={blankMode}
					onClose={onClose}
					badge={badge}
					title={title}
				/>
				<DropdownParentContextProvider parent={menuParent}>
					{children}
				</DropdownParentContextProvider>
			</StyledModalContent>
		</Modal>
	</ModalContext.Provider>;
}
